import { useEffect, useState, useCallback } from "react";
import { Search, Filter, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import CampaignCard from "../components/CampaignCard";
import Seo from "../components/Seo";

const CATEGORIES = ["all", "medical", "education", "emergency", "food", "housing", "community", "disability", "other"];
const URGENCIES = ["all", "critical", "high", "normal", "low"];
const PAGE_SIZE = 12;

export default function Feed() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [urg, setUrg] = useState("all");

  const fetchPage = useCallback(async (targetPage, replace) => {
    if (replace) setLoading(true); else setLoadingMore(true);
    try {
      const params = { status: "approved", page: targetPage, page_size: PAGE_SIZE };
      if (cat !== "all") params.category = cat;
      if (urg !== "all") params.urgency = urg;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/campaigns", { params });
      setItems((prev) => replace ? (data.items || []) : [...prev, ...(data.items || [])]);
      setHasMore(!!data.has_more);
      setTotal(data.total || 0);
      setPage(targetPage);
    } catch {
      if (replace) setItems([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [cat, urg, q]);

  // Reset & re-fetch when filters change
  useEffect(() => { fetchPage(1, true); /* eslint-disable-next-line */ }, [cat, urg]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchPage(1, true);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20">
      <Seo
        title="All live campaigns"
        description="Browse verified Dkarez campaigns by category, urgency or location. Support a story that moves you — every pound makes a difference."
      />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="eyebrow">All live campaigns</span>
        <h1 className="font-serif-display text-4xl sm:text-5xl mt-3 leading-tight">
          Find a story that <span className="italic text-brand-primary">moves you</span>.
        </h1>
        <p className="text-brand-muted mt-4 max-w-2xl">Search, filter, and meet the people behind every cause. Every campaign here has been verified by our team.</p>
      </motion.div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-3 items-stretch">
        <form onSubmit={onSearch} className="md:col-span-2 relative">
          <Search size={18} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, story, or location"
            className="input-premium !pl-11 !py-3.5"
            data-testid="feed-search-input"
          />
        </form>
        <div className="flex gap-3">
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="input-premium !py-3.5" data-testid="feed-category-select">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
          </select>
          <select value={urg} onChange={(e) => setUrg(e.target.value)} className="input-premium !py-3.5" data-testid="feed-urgency-select">
            {URGENCIES.map((u) => <option key={u} value={u}>{u[0].toUpperCase() + u.slice(1)}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-10">
        <FeedResults
          loading={loading}
          items={items}
          total={total}
          hasMore={hasMore}
          loadingMore={loadingMore}
          page={page}
          onLoadMore={() => fetchPage(page + 1, false)}
        />
      </div>
    </div>
  );
}

function FeedResults({ loading, items, total, hasMore, loadingMore, page, onLoadMore }) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="feed-loading">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={`skel-${i}`} className="premium-card h-[440px] animate-pulse">
            <div className="h-56 bg-brand-pink/40" />
            <div className="p-6 space-y-3">
              <div className="h-3 bg-brand-pink/40 rounded w-24" />
              <div className="h-5 bg-brand-pink/40 rounded w-3/4" />
              <div className="h-3 bg-brand-pink/40 rounded w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="premium-card p-16 text-center">
        <Filter className="mx-auto text-brand-primary mb-4" size={28} strokeWidth={1.4} />
        <p className="font-serif-display text-2xl">No campaigns match those filters.</p>
        <p className="text-brand-muted mt-2">Try clearing search or selecting a different category.</p>
      </div>
    );
  }
  return (
    <>
      <div className="flex items-center justify-between mb-6 text-sm text-brand-muted">
        <span>Showing <span className="text-brand-text font-medium">{items.length}</span> of <span className="text-brand-text font-medium">{total}</span> campaigns</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="feed-grid">
        {items.map((c, i) => <CampaignCard key={c.id} campaign={c} index={i % PAGE_SIZE} />)}
      </div>

      {hasMore && (
        <div className="mt-12 flex justify-center">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="btn-outline-gold !py-3 !px-7"
            data-testid="feed-load-more"
          >
            {loadingMore ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" strokeWidth={1.8} />
                Loading…
              </>
            ) : (
              <>Load more campaigns</>
            )}
          </button>
        </div>
      )}
      {!hasMore && items.length > PAGE_SIZE && (
        <p className="text-center text-sm text-brand-muted mt-12">You've reached the end. ✨</p>
      )}
    </>
  );
}
